// Определяем API URL в зависимости от окружения
// Если запрос идет из браузера (client-side), используем конфигурируемый путь
// Если запрос идет из Node.js (server-side), используем внутренние Docker адреса:
// - В dev: http://backend-dev:8080 (прямо к dev backend)
// - В prod: http://backend:80 (прямо к prod backend)

import { getAppEnvKind } from "@/shared/lib/app-env";
import { logger } from "@/shared/lib/logger";

// Безопасная проверка на выполнение в браузере для SSR
const isBrowser =
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof document !== "undefined";

// API URL зависит от окружения и типа клиента
let finalApiUrl: string;

function safeBrowserApiUrl(raw: string | undefined): string {
  const fallback = "/api";
  const candidate = (raw ?? "").trim();
  if (!candidate) return fallback;

  if (candidate.startsWith("/")) return candidate;

  try {
    const u = new URL(candidate);

    if (window.location.protocol === "https:" && u.protocol !== "https:") {
      return fallback;
    }

    const envKind = getAppEnvKind();
    const isDevLike = envKind === "local" || window.location.port === "3000";
    if (isDevLike) return u.toString();

    if (u.origin !== window.location.origin) {
      return fallback;
    }

    return u.toString();
  } catch {
    return fallback;
  }
}

if (isBrowser) {
  const publicUrl = process.env["NEXT_PUBLIC_API_URL"] as string | undefined;
  finalApiUrl = safeBrowserApiUrl(publicUrl);

  // Режим dev-npm: фронт на :3000, backend за Traefik. Раньше здесь относительный
  // «/api» БЕЗУСЛОВНО превращался в `${protocol}//${host}${/api}` — без порта, то есть
  // всегда на :80. Для dev-npm это случайно работало (Traefik локально и правда на :80),
  // но на любом другом стенде (тестовый — :180) браузер уходил на несуществующий
  // http://127.0.0.1/api, CSP резал запрос как cross-origin, axios видел сетевую ошибку и
  // поднимал полноэкранный оверлей «Сайт обновляется» (KAN-283). Приложение выглядело
  // «в деплое», хотя бэкенд был жив — и это же делало браузерные e2e непроходимыми.
  //
  // Теперь абсолютизируем ТОЛЬКО когда домен задан явно и отличается от текущего хоста
  // (порт из него сохраняем). Во всех остальных случаях оставляем относительный путь:
  // он идёт same-origin через rewrites из next.config.mjs (API_PROXY_TARGET), что и
  // безопаснее для кук, и не зависит от того, на каком порту стоит backend.
  const publicDomain =
    (process.env["NEXT_PUBLIC_DOMAIN"] as string | undefined)?.trim() || "";
  if (
    finalApiUrl.startsWith("/") &&
    publicDomain &&
    publicDomain !== window.location.hostname &&
    publicDomain !== window.location.host &&
    (getAppEnvKind() === "local" || window.location.port === "3000")
  ) {
    finalApiUrl = `${window.location.protocol}//${publicDomain}${finalApiUrl}`;
  }
} else {
  const apiUrlFromEnv = process.env["API_URL"] as string | undefined;
  if (apiUrlFromEnv && apiUrlFromEnv.trim().length > 0) {
    finalApiUrl = apiUrlFromEnv;
  } else {
    const fallbackServerUrl =
      (process.env["NEXT_PUBLIC_API_URL"] as string | undefined) ??
      (process.env.NODE_ENV === "production"
        ? "http://backend:80"
        : "http://backend-dev:8080");
    finalApiUrl = fallbackServerUrl;
  }
}

export const apiUrl = finalApiUrl;

// Диагностика адреса API: в проде логгер её не печатает, в dev — уровень debug.
if (process.env["MCP_STDIO"] !== "1") {
  logger.debug("Конфигурация API", {
    apiUrl,
    nodeEnv: process.env.NODE_ENV,
    isBrowser,
    publicApiUrl: process.env["NEXT_PUBLIC_API_URL"],
    proxyTarget: process.env["API_PROXY_TARGET"],
  });
}
